import { LoginRequest, RegisterRequest, UserUpdateRequest, AuthResponse, User, ApiResponse } from '../types/auth';

const API_BASE_URL = '/api';
const TOKEN_KEY = import.meta.env.VITE_JWT_TOKEN_KEY || 'ziweiai_access_token';
const REFRESH_TOKEN_KEY = import.meta.env.VITE_JWT_REFRESH_TOKEN_KEY || 'ziweiai_refresh_token';

class AuthService {
  // 获取存储的访问令牌
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  // 获取存储的刷新令牌
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  // 存储令牌
  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  // 清除令牌
  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  // 创建带认证头的请求配置
  private getAuthHeaders(): HeadersInit {
    const token = this.getAccessToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // 处理API响应
  private async handleResponse<T>(response: Response): Promise<T> {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || '请求失败');
    }
    
    return data.data || data;
  }

  // 用户注册
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    const result = await this.handleResponse<AuthResponse>(response);
    
    // 存储令牌
    this.setTokens(result.tokens.accessToken, result.tokens.refreshToken);
    
    return result;
  }

  // 用户登录
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const result = await this.handleResponse<AuthResponse>(response);
    
    // 存储令牌
    this.setTokens(result.tokens.accessToken, result.tokens.refreshToken);
    
    return result;
  }

  // 刷新访问令牌
  async refreshToken(): Promise<{ accessToken: string; refreshToken: string }> {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('没有刷新令牌');
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    const result = await this.handleResponse<{ accessToken: string; refreshToken: string }>(response);
    
    // 更新存储的令牌
    this.setTokens(result.accessToken, result.refreshToken);
    
    return result;
  }

  // 用户登出
  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      console.error('登出请求失败:', error);
    } finally {
      // 无论请求是否成功，都清除本地令牌
      this.clearTokens();
    }
  }

  // 获取当前用户信息
  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<User>(response);
  }

  // 更新用户信息
  async updateUser(userData: UserUpdateRequest): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    return this.handleResponse<User>(response);
  }

  // 发送密码重置邮件（暂时返回提示信息）
  async resetPassword(email: string): Promise<void> {
    // 这里可以实现发送重置密码邮件的逻辑
    // 目前先抛出一个提示
    throw new Error('密码重置功能暂未实现，请联系管理员');
  }

  // 检查用户是否已登录
  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // 带自动刷新令牌的请求方法
  async authenticatedRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });

      if (response.status === 401) {
        // 尝试刷新令牌
        try {
          await this.refreshToken();
          
          // 重新发送请求
          const retryResponse = await fetch(url, {
            ...options,
            headers: {
              ...this.getAuthHeaders(),
              ...options.headers,
            },
          });

          return this.handleResponse<T>(retryResponse);
        } catch (refreshError) {
          // 刷新失败，清除令牌并抛出错误
          this.clearTokens();
          throw new Error('登录已过期，请重新登录');
        }
      }

      return this.handleResponse<T>(response);
    } catch (error) {
      throw error;
    }
  }
}

export default new AuthService();